import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { formatDate } from "@/lib/utils"
import { Head, router, usePage } from "@inertiajs/react"
import { IconArrowLeft, IconExternalLink, IconFileText, IconPencil } from "@tabler/icons-react"

interface LineItem {
  id: number
  description: string
  employee_name: string | null
  quantity: number
  unit_price: number
  discount_percentage: number | null
  tax_percentage: number | null
  total_amount: number
}

interface StatusAction {
  name: string
  action: string
  color: string
}

interface Invoice {
  id: number
  invoice_number: string
  status: string
  invoice_date: string
  due_date: string | null
  date_paid: string | null
  currency: string
  currency_symbol: string
  total_amount: number
  subtotal: number
  discount_amount: number
  tax_amount: number
  notes: string | null
  invoice_pdf_url: string | null
  sent_to_accounting: boolean
  editable: boolean
  customer: { id: number; name: string }
  project: { id: number; name: string; customer_id: number } | null
  line_items: LineItem[]
  available_actions: StatusAction[]
  can_update: boolean
}

interface Props {
  [key: string]: unknown
  invoice: Invoice
}

const STATUS_BADGE: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; label: string }> = {
  paid: { variant: "default", label: "Paid" },
  sent: { variant: "secondary", label: "Sent" },
  overdue: { variant: "destructive", label: "Overdue" },
  draft: { variant: "outline", label: "Draft" },
  cancelled: { variant: "outline", label: "Cancelled" },
}

function formatCurrency(amount: number, symbol: string) {
  return `${symbol}${amount.toFixed(2)}`
}

function formatPct(val: number | null) {
  if (!val || val <= 0) return "\u2014"
  return `${val.toFixed(1)}%`
}

export default function InvoiceShow() {
  const { invoice } = usePage<Props>().props
  const badge = STATUS_BADGE[invoice.status] || { variant: "outline" as const, label: invoice.status }

  function handleAction(action: string) {
    router.patch(`/invoices/${invoice.id}/${action}`)
  }

  return (
    <>
      <Head title={`${invoice.invoice_number} - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        {/* Header */}
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/invoices">Invoices</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{invoice.invoice_number}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                <a href="/invoices">
                  <IconArrowLeft className="h-4 w-4" />
                </a>
              </Button>
              <div>
                <div className="flex items-center gap-2.5">
                  <h1 className="text-lg font-semibold tracking-tight">{invoice.invoice_number}</h1>
                  <Badge variant={badge.variant}>{badge.label}</Badge>
                </div>
                <p className="text-muted-foreground text-sm">
                  <a href={`/customers/${invoice.customer.id}`} className="hover:text-foreground transition-colors">
                    {invoice.customer.name}
                  </a>
                  {invoice.project && (
                    <>
                      {" "}
                      &middot;{" "}
                      <a href={`/projects/${invoice.project.id}`} className="hover:text-foreground transition-colors">
                        {invoice.project.name}
                      </a>
                    </>
                  )}
                </p>
              </div>
            </div>

            {invoice.can_update && (
              <div className="flex items-center gap-2">
                {invoice.available_actions.map((action) => (
                  <Button
                    key={action.action}
                    variant={action.color === "green" ? "default" : action.color === "red" ? "destructive" : "outline"}
                    size="sm"
                    onClick={() => handleAction(action.action)}
                  >
                    {action.name}
                  </Button>
                ))}
                <Button variant="outline" size="sm" asChild>
                  <a href={`/invoices/${invoice.id}/quick_edit`}>
                    <IconPencil className="mr-1.5 h-4 w-4" />
                    Quick Edit
                  </a>
                </Button>
                {invoice.editable && (
                  <Button size="sm" asChild>
                    <a href={`/invoices/${invoice.id}/edit`}>
                      <IconPencil className="mr-1.5 h-4 w-4" />
                      Edit
                    </a>
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Details Grid */}
        <div className="rounded-xl border bg-card">
          <div className="grid grid-cols-2 gap-px sm:grid-cols-3 lg:grid-cols-6">
            <DetailCell label="Invoice Date" value={formatDate(invoice.invoice_date)} />
            <DetailCell label="Due Date" value={formatDate(invoice.due_date)} />
            <DetailCell label="Date Paid" value={formatDate(invoice.date_paid)} />
            <DetailCell label="Currency" value={invoice.currency.toUpperCase()} />
            <DetailCell label="Accounting" value={invoice.sent_to_accounting ? "Sent" : "Not sent"} />
            <DetailCell label="Total" value={formatCurrency(invoice.total_amount, invoice.currency_symbol)} bold />
          </div>

          {invoice.invoice_pdf_url && (
            <>
              <Separator />
              <div className="p-5">
                <a
                  href={invoice.invoice_pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80 inline-flex items-center gap-1.5 text-sm font-medium"
                >
                  <IconFileText className="h-4 w-4" />
                  View Invoice PDF
                  <IconExternalLink className="h-3 w-3" />
                </a>
              </div>
            </>
          )}

          {invoice.notes && (
            <>
              <Separator />
              <div className="p-5">
                <p className="text-muted-foreground mb-1 text-xs font-medium uppercase tracking-wider">Notes</p>
                <p className="text-sm leading-relaxed">{invoice.notes}</p>
              </div>
            </>
          )}
        </div>

        {/* Line Items */}
        <div className="rounded-xl border bg-card">
          <div className="p-5 pb-0">
            <h3 className="text-sm font-medium">Line Items</h3>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead className="text-right">Disc</TableHead>
                  <TableHead className="text-right">Tax</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoice.line_items.length > 0 ? (
                  invoice.line_items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="max-w-[300px]">{item.description}</TableCell>
                      <TableCell className="text-muted-foreground">{item.employee_name || "\u2014"}</TableCell>
                      <TableCell className="text-right tabular-nums">{item.quantity}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(item.unit_price, invoice.currency_symbol)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-right tabular-nums">
                        {formatPct(item.discount_percentage)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-right tabular-nums">
                        {formatPct(item.tax_percentage)}
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {formatCurrency(item.total_amount, invoice.currency_symbol)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-muted-foreground text-center py-8">
                      No line items
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Totals */}
          <Separator />
          <div className="flex justify-end p-5">
            <dl className="w-full max-w-xs space-y-1.5 text-sm">
              <TotalRow label="Subtotal" value={formatCurrency(invoice.subtotal, invoice.currency_symbol)} />
              <TotalRow
                label="Discounts"
                value={`-${formatCurrency(invoice.discount_amount, invoice.currency_symbol)}`}
                muted
              />
              <TotalRow label="Taxes" value={formatCurrency(invoice.tax_amount, invoice.currency_symbol)} muted />
              <Separator className="!my-2" />
              <div className="flex items-center justify-between font-semibold">
                <dt>Total</dt>
                <dd className="tabular-nums">{formatCurrency(invoice.total_amount, invoice.currency_symbol)}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </>
  )
}

function DetailCell({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="p-5">
      <p className="text-muted-foreground mb-1 text-xs font-medium uppercase tracking-wider">{label}</p>
      <p className={`text-sm tabular-nums ${bold ? "text-lg font-semibold" : "font-medium"}`}>{value}</p>
    </div>
  )
}

function TotalRow({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <dt className={muted ? "text-muted-foreground" : ""}>{label}</dt>
      <dd className={`tabular-nums ${muted ? "text-muted-foreground" : ""}`}>{value}</dd>
    </div>
  )
}

InvoiceShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
